/** @jsxImportSource @emotion/react */
"use client";

import NiceModal, { useModal } from "@ebay/nice-modal-react";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { AnalyticsEvent } from "@packages/analytics";
import { useAnalytics } from "@packages/analytics";
import type { Images } from "@packages/sdk";
import { Fragment, useEffect } from "react";

import {
  constantSemanticColors,
  radius,
  spacing,
  zIndices,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { HallowImage } from "../../../blocks/other/HallowImage/HallowImage";
import { ShareButtons } from "../../../blocks/other/ShareButtons/ShareButtons.emotion";
import type { MaskProps } from "../../_core/Mask/Mask.emotion";
import { Mask } from "../../_core/Mask/Mask.emotion";
import { Text } from "../../_core/Text/Text.emotion";
import { HallowLogoText } from "../../icons/HallowLogoText/HallowLogoText.emotion";
import type { ModalProps } from "../Modal/Modal.emotion";
import { Modal } from "../Modal/Modal.emotion";
import { ModalHeader } from "../ModalHeader/ModalHeader.emotion";

const hallowLogoTextStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  height: 10,
  margin: "auto",
};
const imageStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderTopLeftRadius: radius.l,
  borderTopRightRadius: radius.l,
  height: spacing.full,
  left: 0,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  position: "absolute",
  top: 0,
  userSelect: "none",
  width: spacing.full,
};
const imageContainerStyle: Interpolation = {
  paddingBottom: "min(86.93181818%, 300px)",
  position: "relative",
  width: spacing.full,
};
const maskStyle: Interpolation = {
  bottom: -1,
  position: "absolute",
};
const modalContentStyle: Interpolation = {
  paddingLeft: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.l,
    paddingRight: spacing.l,
    width: spacing.fit,
  },
  paddingRight: spacing.none,
  width: spacing.full,
  zIndex: zIndices.modalContent,
};
const modalHeaderStyle: Interpolation = {
  paddingTop: spacing.m,
};
const shareButtonsStyle: Interpolation = {
  paddingBottom: spacing.s,
  paddingLeft: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.none,
    paddingRight: spacing.none,
  },
  paddingRight: spacing.m,
  paddingTop: spacing.s,
};
const textContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  paddingBottom: spacing.l,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
};
const textContainerTopStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
  maxWidth: "none",
  [cssMediaQueryMinWidthTablet]: { maxWidth: 416 },
};
const titleStyle: Interpolation = {
  color: constantSemanticColors.onColorHighest,
  textAlign: "center",
};
const topContainerStyle: Interpolation<{ color: string }> = (props) => ({
  backgroundColor: props.color,
  borderRadius: radius.l,
  marginLeft: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    marginLeft: spacing.none,
    marginRight: spacing.none,
  },
  marginRight: spacing.m,
});

export type ShareModalProps = ModalProps & {
  analyticsEvent?: AnalyticsEvent;
  color: string;
  description?: string;
  imgSrc: string | Images;
  link?: string;
  type?: "campaign";
  title: string;
  imgCSS?: Interpolation;
  contentContainerCSS?: Interpolation;
};

export const ShareModal = NiceModal.create(
  ({
    analyticsEvent,
    children,
    color,
    description,
    imgSrc,
    link,
    type,
    title,
    imgCSS = {},
    ...props
  }: ShareModalProps) => {
    const modal = useModal();
    const analytics = useAnalytics();
    const t = useTranslations();

    useEffect(() => {
      if (analyticsEvent) {
        analytics.track(analyticsEvent);
      }
    }, []);

    return (
      <Modal
        {...props}
        contentCSS={[modalContentStyle, props.contentCSS]}
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) {
            return modal.show();
          }

          modal.resolve();
          return modal.hide();
        }}
      >
        <ModalHeader css={modalHeaderStyle}>{t("campaign_share")}</ModalHeader>
        <div css={[topContainerStyle({ color }), props.contentContainerCSS]}>
          {children ? (
            <Fragment>
              {children}
              <div css={textContainerStyle}>
                <HallowLogoText css={hallowLogoTextStyle} />
              </div>
            </Fragment>
          ) : (
            <Fragment>
              <div css={imageContainerStyle}>
                <HallowImage
                  alt={title}
                  src={
                    typeof imgSrc === "string" ? { fallback: imgSrc } : imgSrc
                  }
                  size={"l"}
                  css={[imageStyle, imgCSS]}
                />
                <Mask
                  color={color as MaskProps["color"]}
                  direction="to top"
                  css={maskStyle}
                />
              </div>
              <div css={textContainerStyle}>
                <div css={textContainerTopStyle}>
                  <Text
                    overflow="ellipsis"
                    size="l"
                    type="title"
                    css={titleStyle}
                    title={title}
                  >
                    {title}
                  </Text>
                  {description && (
                    <Text
                      overflow="ellipsis"
                      size="xl"
                      type="detail"
                      css={titleStyle}
                      title={description}
                    >
                      {description}
                    </Text>
                  )}
                </div>
                <HallowLogoText css={hallowLogoTextStyle} />
              </div>
            </Fragment>
          )}
        </div>
        <ShareButtons
          description={description}
          link={link}
          imgSrc={
            !imgSrc
              ? null
              : typeof imgSrc === "string"
                ? imgSrc
                : imgSrc.fallback
          }
          title={title}
          type={type}
          css={shareButtonsStyle}
        />
      </Modal>
    );
  },
);
